import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog } from '@material-ui/core';
import _uniq from 'lodash-es/uniq';
import _uniqBy from 'lodash-es/uniqBy';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { capitalize } from 'core/helpers/formatters';
import { useStore } from 'core/store';
import offerUpdateIcon from './assets/offer-update.svg';
import { getNewPricing, itemsChangedMap } from './helpers';
import useStyles from './modal-offer-diff.styles';

const OfferDifferenceModal = ({ open }) => {
  const classes = useStyles();

  const {
    offer: { offer, offerDifference, clearOfferDifference }
  } = useStore();

  const newPricing = getNewPricing(offer);

  // Check if there are any meaningful changes to display
  const hasItemChanges = offerDifference?.diffKeys?.full?.some((change) => itemsChangedMap[change]);
  const hasEligibilityChanges = offerDifference?.eligibilityChanges?.some(
    (change) => change.policyType === 'auto' || change.policyType === 'home'
  );
  const showModal = hasItemChanges || hasEligibilityChanges;

  // Don't render the modal if there are no eligiblity or items changes to display
  if (!showModal) {
    return null;
  }

  return (
    <Dialog open={open} classes={{ paper: classes.coveragesInfo }}>
      <img src={offerUpdateIcon} className={classes.icon} alt="Paper with two arrows forming a circle" />
      <Label type="h1">This offer's coverages and prices have been updated</Label>
      <div className={classes.content}>
        <Label className={classes.contentLabel} type="body1">
          We've run reports on this offer and have revised the following value(s) based on information from our trusted
          data sources:
        </Label>

        {/* item changes */}
        <ul className={classes.changes}>
          {_uniq(offerDifference.diffKeys.full).map(
            (change) =>
              itemsChangedMap[change] && (
                <li key={change}>
                  <Label className={classes.contentLabel} type="body1">
                    {itemsChangedMap[change]}
                  </Label>
                </li>
              )
          )}

          {/* eligibility changes */}
          {_uniqBy(offerDifference.eligibilityChanges, 'policyType').map(
            (change) =>
              (change.policyType === 'auto' || change.policyType === 'home') && (
                <li key={change.policyType}>
                  <Label className={classes.contentLabel} type="body1">
                    {change.isEligible
                      ? capitalize(`${change.policyType} Eligible`)
                      : capitalize(`${change.policyType} Not Eligible`)}
                  </Label>
                </li>
              )
          )}

          {/* new pricing */}
          {newPricing?.auto?.total && (
            <li key="auto-price">
              <Label className={classes.contentLabel} type="body1">
                Auto pricing: ${newPricing.auto.total.toFixed(2)} for {newPricing.auto.period}
              </Label>
            </li>
          )}
          {newPricing?.home?.total && (
            <li key="home-price">
              <Label className={classes.contentLabel} type="body1">
                Home pricing: ${newPricing.home.total.toFixed(2)} for {newPricing.home.period}
              </Label>
            </li>
          )}
        </ul>
      </div>
      <Button className={classes.button} variant="contained" color="primary" onClick={() => clearOfferDifference()}>
        Okay, got it
      </Button>
    </Dialog>
  );
};

OfferDifferenceModal.propTypes = {
  open: PropTypes.bool.isRequired
};

export default observer(OfferDifferenceModal);
